import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { productsAPI, pricesAPI } from '../../services/api';
import Button from '../../common/components/Button';
import Card from '../../common/components/Card';
import Loading from '../../common/components/Loading';
import CategoryDialog from '../../common/components/CategoryDialog';
import ProductWizardDialog from '../../common/components/ProductWizardDialog';
import ProductDetailsDialog from '../../common/components/ProductDetailsDialog';
import { Plus, Package, FolderOpen, ChevronDown, ChevronUp, Settings, Eye } from 'lucide-react';
import { formatCurrency } from '../../common/utils/formatters';
import './ProductsScreen.css';

const ProductsScreen = () => {
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [viewingProductId, setViewingProductId] = useState(null);
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingSizeId, setEditingSizeId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  const { data: products, isLoading: productsLoading } = useQuery('products', async () => {
    const response = await productsAPI.list();
    return response.data.data;
  });

  const { data: prices } = useQuery('prices', async () => {
    const response = await pricesAPI.list();
    return response.data.data;
  });

  const handleAddProduct = () => {
    setEditingProductId(null);
    setEditingSizeId(null);
    setProductDialogOpen(true);
  };

  const handleViewProduct = (product) => {
    setViewingProductId(product.id);
    setDetailsDialogOpen(true);
  };

  const handleCloseWizard = () => {
    setProductDialogOpen(false);
    setEditingProductId(null);
    setEditingSizeId(null);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setViewingProductId(null);
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  if (productsLoading) {
    return <Loading message="Loading products..." />;
  }

  const productsList = products || [];

  // Group products by category
  const groupedProducts = productsList.reduce((acc, product) => {
    const categoryKey = product.category_name || '_uncategorized';
    if (!acc[categoryKey]) {
      acc[categoryKey] = [];
    }
    acc[categoryKey].push(product);
    return acc;
  }, {});

  // Get price info for a product
  const getProductPrices = (productId) => {
    return prices?.filter(p => p.product_id === productId) || [];
  };

  const getPriceRange = (productId) => {
    const productPrices = getProductPrices(productId);
    if (!productPrices.length) return 'No prices set';
    
    const priceValues = productPrices.map(p => p.price);
    const min = Math.min(...priceValues);
    const max = Math.max(...priceValues);
    
    if (min === max) {
      return formatCurrency(min);
    }
    return `${formatCurrency(min)} - ${formatCurrency(max)}`;
  };

  const categoryKeys = Object.keys(groupedProducts).sort((a, b) => {
    if (a === '_uncategorized') return 1;
    if (b === '_uncategorized') return -1;
    return a.localeCompare(b);
  });

  return (
    <div className="products-screen">
      <div className="screen-header">
        <div>
          <h1>Products</h1>
          <p>Manage your product catalog</p>
        </div>
        <div className="header-actions">
          <Button variant="outline" onClick={() => setCategoryDialogOpen(true)}>
            <Settings size={20} style={{ marginRight: '8px' }} />
            Manage Categories
          </Button>
          <Button onClick={handleAddProduct}>
            <Plus size={20} style={{ marginRight: '8px' }} />
            Add Product
          </Button>
        </div>
      </div>

      {productsList.length === 0 ? (
        <Card className="empty-state">
          <Package size={48} className="empty-state-icon" />
          <h3>No products yet</h3>
          <p>Get started by adding your first product</p>
          <Button onClick={handleAddProduct}>
            <Plus size={20} style={{ marginRight: '8px' }} />
            Add Product
          </Button>
        </Card>
      ) : (
        <div className="products-by-category">
          {categoryKeys.map((categoryKey) => {
            const categoryName = categoryKey === '_uncategorized' ? 'Uncategorized' : categoryKey;
            const categoryProducts = groupedProducts[categoryKey];
            const isExpanded = expandedCategories[categoryKey] !== false;

            return (
              <div key={categoryKey} className="category-section">
                <div className="category-header" onClick={() => toggleCategory(categoryKey)}>
                  <div className="category-title">
                    <FolderOpen size={20} />
                    <h2>{categoryName}</h2>
                    <span className="category-count">({categoryProducts.length})</span>
                  </div>
                  <button className="toggle-button">
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                </div>

                {isExpanded && (
                  <div className="products-grid">
                    {categoryProducts.map((product) => (
                      <Card key={product.id} className="product-card">
                        <div className="product-card-main">
                          <div className="product-card-header">
                            <div className="product-card-info">
                              <h3 className="product-card-name">{product.name}</h3>
                              {product.category_name && (
                                <p className="product-card-category">{product.category_name}</p>
                              )}
                            </div>
                            <span className={`status-badge ${product.is_active ? 'active' : 'inactive'}`}>
                              {product.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>

                          {product.description && (
                            <p className="product-card-description">{product.description}</p>
                          )}
                        </div>

                        <div className="product-card-pricing">
                          <span className="pricing-label">Price Range</span>
                          <span className="pricing-value">{getPriceRange(product.id)}</span>
                        </div>

                        <div className="product-card-sizes">
                          <p className="sizes-label">Available Sizes:</p>
                          {getProductPrices(product.id).map((price) => (
                            <div key={price.id} className="size-chip">
                              <span className="size-name">{price.size_option_name}</span>
                              <span className="size-price">{formatCurrency(price.price)}</span>
                            </div>
                          ))}
                        </div>

                        <div className="product-card-actions">
                          <Button variant="outline" onClick={() => handleViewProduct(product)} size="small">
                            <Eye size={16} style={{ marginRight: '8px' }} />
                            View Details
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CategoryDialog
        isOpen={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
      />

      <ProductWizardDialog
        isOpen={productDialogOpen}
        onClose={handleCloseWizard}
        productId={editingProductId}
        sizeId={editingSizeId}
      />

      <ProductDetailsDialog
        isOpen={detailsDialogOpen}
        onClose={handleCloseDetails}
        productId={viewingProductId}
      />
    </div>
  );
};

export default ProductsScreen;
